import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clock from "../assets/img/clock.svg";
import defaultImg from "../assets/img/pizzalabel.svg";
import { FaHeart } from "react-icons/fa";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import {
  addLikedItem,
  getLikedItems,
} from "../store/actions/restaurantsAction";
import ProductDetailModal from "../utils/ProductDetailModal";
import RestaurantLoader from "../utils/RestaurantLoader";

const ProductImage = ({
  src,
  alt,
  color,
  onClick,
  defaultImg,
  isLiked,
  onHeartClick,
}) => (
  <div style={{ position: "relative" }}>
    <img
      alt={alt}
      className="product_profile cursor-pointer"
      style={{ height: "120px", objectFit: "cover", width: "100%" }}
      src={src || defaultImg}
      onClick={onClick}
    />
    <div
      style={{
        position: "absolute",
        top: "0px",
        left: "0px",
        cursor: "pointer",
        zIndex: 1,
        width: "24px",
        height: "24px",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onHeartClick();
      }}
    >
      <FaHeart
        size={18}
        color={isLiked ? color : color}
        fill={isLiked ? color : color}
        style={{
          transition: "color 0.3s ease",
          opacity: isLiked ? 1 : 0.6,
          cursor: "pointer",
        }}
      />
    </div>
  </div>
);

const PlusIcon = ({ color, onClick }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="plus_icon"
    style={{
      marginTop: "-12px",
      marginRight: "-9px",
      cursor: "pointer",
    }}
    onClick={onClick}
  >
    <rect
      x="0.5"
      y="0.5"
      width="26.2554"
      height="26.2554"
      rx="13.1277"
      fill={color}
    />
    <path
      d="M12.6727 19.2554V8H14.5827V19.2554H12.6727ZM8 14.5827V12.6727H19.2554V14.5827H8Z"
      fill="white"
    />
    <rect
      x="0.5"
      y="0.5"
      width="26.2554"
      height="26.2554"
      rx="13.1277"
      stroke="white"
    />
  </svg>
);

const Labels = ({ labels, darkMode }) => (
  <div className="d-flex flex-wrap mb-2 cursor-pointer labels-wrapper">
    {labels?.slice(0, 2)?.map((itm, idx) => (
      <div
        key={itm?.id}
        className="d-flex align-items-center label-item me-2 mb-2 px-1"
        style={{
          flexDirection: "column",
          maxWidth: "90px",
          textAlign: "center",
        }}
      >
        {itm?.labelURL && (
          <>
            <img
              alt={itm?.label}
              id={`label_${idx}_${itm.id}`}
              className="mb-1"
              src={itm?.labelURL}
              width={25}
              height={25}
              style={{ objectFit: "contain" }}
            />
            <UncontrolledTooltip
              placement="bottom"
              target={`label_${idx}_${itm.id}`}
            >
              {itm?.label}
            </UncontrolledTooltip>
          </>
        )}
      </div>
    ))}
  </div>
);

const CookingTime = ({ time, darkMode, onClick }) => (
  <div
    className="d-flex align-items-center cursor-pointer"
    title={`Cooking Time ${time}`}
    onClick={onClick}
  >
    <img
      alt="clock"
      src={clock}
      width={15}
      height={15}
      className={`mr-1 ${darkMode ? "helper-text-dark" : "helper-text"}`}
    />
    <h6
      className={`mb-0 fs--10 f fw--400 ${
        darkMode ? "helper-text-dark" : "helper-text"
      }`}
    >
      {`${time} mins`}
    </h6>
  </div>
);

const CardItem = ({ data, title, categoryId }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { menuLoading } = useSelector((state) => state.parentMenu);
  const { darkMode, color } = useSelector((state) => state.global);
  const { LikedMenu, restaurantsDetail } = useSelector(
    (state) => state.restaurants
  );
  const { cartDetails } = useSelector((state) => state.cart);
  const currency = restaurantsDetail?.currency;
  const [searchTerm, setSearchTerm] = useState("");
  const [menuData, setMenuData] = useState(data);
  const { uid } = useSelector((state) => state.authUser);
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (item) => {
    setModalData(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleLikeItem = (item) => {
    dispatch(addLikedItem(item?.id, uid, cartDetails?.menuId, categoryId?.id));
  };

  const handleClickProduct = useCallback(
    (item) => {
      const detail = item?.menuDetail || item;
      history.push({
        pathname: `/productDetails/${item.id}`,
        state: {
          ...detail,
          currency: item?.currency || detail?.currency,
          labels: detail?.labels || [],
          recommendedProducts: detail?.recommendedProducts || [],
        },
      });
    },
    [history]
  );

  const handleViewAll = useCallback(() => {
    history.push({
      pathname: "/viewall-product",
      state: { data: menuData, categoryId: categoryId },
    });
  }, [history, menuData, categoryId]);

  useEffect(() => {
    dispatch(getLikedItems(uid, cartDetails?.menuId, categoryId?.id));
  }, [data, categoryId, dispatch, uid, cartDetails?.menuId]);

  useEffect(() => {
    if (searchTerm !== "") {
      const filteredMenu = data.filter((f) => {
        const name =
          typeof f?.name === "string"
            ? f.name
            : typeof f?.menuDetail?.name === "string"
            ? f.menuDetail.name
            : "";

        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setMenuData(filteredMenu);
    } else {
      setMenuData(data);
    }
  }, [searchTerm, data]);

  if (menuLoading) {
    return (
      <Container className="most_popular py-3">
        <Row className="py-5 justify-content-center position-relative">
          <RestaurantLoader message="Fetching Menus Items" />
        </Row>
      </Container>
    );
  }

  return (
    <Container className="most_popular py-3">
      <Row className="d-flex justify-content-between align-items-center">
        <Col lg={7} md={6}>
          <h4
            className={
              darkMode
                ? "fs--18 fw--600 mb-0 normal-heading-dark"
                : "fs--18 fw--600 mb-0 normal-heading"
            }
          >
            {title}
          </h4>
        </Col>
        <Col
          lg={5}
          md={6}
          className="py-3 d-flex align-items-center justify-content-end"
          style={{ gap: "10px" }}
        >
          <div className="input-group border rounded-pill py-1 px-2 w-75 bg-light me-3">
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search with the item name...."
              className="fs--14 form-control bg-light border-0"
            />
          </div>

          {location.pathname !== "/viewall-product" && (
            <>
              {menuData?.length > 4 && (
                <span
                  className="ms-2 cursor-pointer fw-semibold"
                  style={{
                    color,
                    fontSize: "0.9rem",
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleViewAll}
                >
                  View All Items
                </span>
              )}
            </>
          )}
        </Col>
      </Row>

      <Row className="mt-3 justify-content-center position-relative">
        {(menuData?.length > 0
          ? location.pathname === "/viewall-product"
            ? menuData
            : menuData.slice(0, 4)
          : []
        ).map((item) => (
          <Col
            key={item.id}
            className={
              darkMode
                ? "d-flex most_popular__card-dark"
                : "d-flex most_popular__card"
            }
            md={4}
            sm={4}
            lg={4}
            xl={3}
            xs={6}
          >
            <Card className="p-1 m-2">
              <ProductImage
                src={
                  item?.images?.[0] ||
                  item?.menuDetail?.images?.[0] ||
                  defaultImg
                }
                alt="Sample"
                onClick={() => handleClickProduct(item)}
                defaultImg={defaultImg}
                color={color}
                isLiked={LikedMenu.some(
                  (likedItem) => likedItem?.itemId === item.id
                )}
                onHeartClick={() => handleLikeItem(item)}
              />
              <CardBody className="px-2 py-2">
                {(item?.variants?.length ||
                  item?.menuDetail?.variants?.length) > 0 && (
                  <div className="mb-2">
                    {(item?.variants || item?.menuDetail?.variants)?.map(
                      (variant, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center px-1 py-1"
                          style={{
                            backgroundColor: darkMode ? "#2c2c2c" : "#f8f9fa",
                            borderRadius: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <span
                            className="fs--12 fw--500 text-capitalize"
                            style={{ color: darkMode ? "#ddd" : "#444" }}
                          >
                            {variant?.size || ""}
                            {variant?.calories &&
                              ` · 🧬 ${variant.calories} kcal`}
                          </span>

                          {variant?.price && (
                            <span
                              className="fs--13 fw--600"
                              style={{ color, whiteSpace: "nowrap" }}
                            >
                              {currency || "$"} {variant.price}
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div style={{ flex: 1 }}>
                    <CardTitle
                      tag="h5"
                      className="mb-1 fs--15 fw--700 text-capitalize cursor-pointer text-truncate"
                      title={item?.name || item?.menuDetail?.name}
                      onClick={() => handleClickProduct(item)}
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.3",
                      }}
                    >
                      {item?.name || item?.menuDetail?.name}
                    </CardTitle>
                    <div className="mt-1">
                      <CookingTime
                        time={
                          item?.estimatedTime || item?.menuDetail?.estimatedTime
                        }
                        darkMode={darkMode}
                        onClick={() => handleClickProduct(item)}
                      />
                    </div>
                  </div>

                  <div
                    className="ms-2 d-flex flex-column align-items-end justify-content-start"
                    style={{ minHeight: "100%" }}
                  >
                    <Labels
                      labels={item?.labels || item?.menuDetail?.labels}
                      darkMode={darkMode}
                    />
                  </div>
                </div>
              </CardBody>

              <PlusIcon
                color={color}
                onClick={() => handleClickProduct(item)}
              />

              <div className="text-center mb-2">
                <button
                  onClick={() => handleOpenModal(item)}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    padding: "6px 16px",
                    fontSize: "14px",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: "6px",
                    opacity: 0.85,
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = 1;
                    e.currentTarget.style.boxShadow =
                      "0 4px 10px rgba(0,0,0,0.2)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = 0.85;
                    e.currentTarget.style.boxShadow =
                      "0 2px 6px rgba(0,0,0,0.15)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  View Item Detail
                </button>
              </div>
            </Card>
          </Col>
        ))}

        {menuData?.length === 0 && (
          <div className="w-100 text-center py-5">
            <h6
              className={`mb-0 fs--16 fw--800 ${
                darkMode ? "helper-text-dark" : "helper-text"
              }`}
            >
              No Item Found.
            </h6>
          </div>
        )}

        {modalData && (
          <ProductDetailModal
            isOpen={isModalOpen}
            toggle={handleCloseModal}
            data={modalData}
            darkMode={darkMode}
            color={color}
            currency={currency}
          />
        )}
      </Row>
    </Container>
  );
};

export default CardItem;
